<template>
  <div>
    <v-card outlined rounded="lg" class="mb-4 pa-2 pa-md-6" color="grey-8">
      <v-card-title class="d-flex justify-space-between pa-md-4">
        <span class="text-body-1 black--text font-weight-medium">訊息</span>
        <v-btn v-if="showDelete" icon @click="$emit('remove')">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="d-flex flex-column" style="row-gap: 10px;">
        <div v-if="type === 'post-op'" class="d-flex align-center flex-wrap" style="gap: 8px;">
          <div class="black--text font-weight-medium" style="min-width: 70px;">細項名稱</div>
          <v-text-field
            v-model="editableMessage.content.title"
            :rules="rules.title"
            outlined
            background-color="white"
            dense
            hide-details="auto"
            maxlength="50"
            counter="50"
            placeholder="供內部管理人員參考"
            style="max-width: 300px;"
          />
        </div>

        <div class="d-flex align-center flex-wrap" style="gap: 8px;">
          <div class="black--text font-weight-medium">訊息樣式</div>
          <v-select
            :items="messageStyleOptions"
            v-model="editableMessage.type"
            outlined
            background-color="white"
            dense
            hide-details="auto"
          />
        </div>

        <div class="d-flex flex-column" style="gap: 10px;">
          <div
            v-if="editableMessage.type === 'line_text_image'"
            key="line-text-image"
            class="d-flex flex-column"
            style="gap: 10px;"
          >
            <div class="d-flex flex-column" style="gap: 8px;">
              <div class="black--text font-weight-medium">文字內容</div>
              <v-textarea
                v-model="editableMessage.content.text"
                :rules="rules.textImageText"
                :counter="type === 'post-op' ? 1000 : undefined"
                outlined
                background-color="white"
                :hide-details="type === 'post-op' ? false : 'auto'"
              />
            </div>
            <div class="d-flex flex-column" style="gap: 8px;">
              <div class="black--text font-weight-medium">圖片內容（最多 {{ imgLimit }} 張圖片，格式限 JPG/PNG）</div>
              <v-sheet max-height="200" class="overflow-y-auto d-flex flex-wrap" style="gap: 8px;" color="transparent">
                <v-sheet
                  v-for="(img, index) in editableMessage.content.images"
                  :key="index"
                  max-width="150"
                  max-height="150"
                  style="position: relative; cursor: pointer;"
                  class="overflow-hidden d-flex align-center justify-center"
                  rounded
                  @click="openImageDialog(getImgSrc(img))"
                >
                  <v-btn
                    color="white"
                    fab
                    absolute
                    right
                    top
                    width="24"
                    height="24"
                    light
                    style="right: 0; top: 0; z-index: 2;"
                    @click.stop="onImageRemoved(index)"
                  >
                    <v-icon size="20">
                      mdi-close
                    </v-icon>
                  </v-btn>
                  <v-img
                    :src="getImgSrc(img)"
                    max-width="150"
                    max-height="150"
                    contain
                  />
                </v-sheet>
                <ImageUploadTool
                  v-if="editableMessage.content.images.length < imgLimit"
                  :max-limit="imgLimit - editableMessage.content.images.length"
                  :max-file-size="2 * 1024 * 1024"
                  @submit="onImageAdded"
                >
                  <v-hover v-slot="{ hover }">
                    <v-sheet
                      width="150"
                      height="150"
                      class="d-flex flex-column align-center justify-center"
                      role='button'
                      style="gap: 1rem; position: relative"
                    >
                      <v-overlay :value="hover" absolute opacity="0.3">
                        <v-icon color="white" sm>
                          mdi-camera
                        </v-icon>
                      </v-overlay>
                      <v-icon size="30" color="grey lighten-1" style="flex:0 0 0">
                        mdi-image-area
                      </v-icon>
                      <p class="text-subtitle-2">
                        上傳圖片
                      </p>
                    </v-sheet>
                  </v-hover>
                </ImageUploadTool>
              </v-sheet>
            </div>
          </div>

          <div
            v-if="editableMessage.type === 'line_flex'"
            key="line-flex"
            class="d-flex flex-column"
            style="gap: 10px;"
          >
            <div class="d-flex flex-column" style="gap: 8px;">
              <div class="black--text font-weight-medium">訊息內容</div>
              <v-textarea
                v-model="editableMessage.content.json"
                :rules="rules.json"
                placeholder="客製化 JSON"
                outlined
                background-color="white"
                hide-details="auto"
              />
            </div>
          </div>

          <div
            v-if="editableMessage.type === 'line_default'"
            key="line-default"
            class="d-flex flex-column"
            style="gap: 10px;"
          >
            <div class="d-flex flex-column" style="gap: 8px;">
              <div class="black--text font-weight-medium">訊息內容</div>
              <v-textarea
                v-model="editableMessage.content.text"
                :rules="rules.defaultText"
                outlined
                background-color="white"
                hide-details="auto"
              />
            </div>
            <div class="d-flex flex-column" style="gap: 8px;">
              <div class="black--text font-weight-medium">現成卡片示意圖（點擊圖片顯示完整卡片內容）</div>
              <v-sheet
                max-width="150"
                max-height="150"
                class="overflow-hidden d-flex align-center justify-center"
                rounded
                role="button"
                style="cursor: pointer;"
                @click="openImageDialog(assetURL('/asset/crm/PostOperativeCareDefaultCard.png'))"
              >
                <v-img
                  :src="assetURL('/asset/crm/PostOperativeCareDefaultCard.png')"
                  max-width="150"
                  max-height="150"
                  contain
                />
              </v-sheet>
            </div>
          </div>

          <div
            v-if="editableMessage.type === 'line_default_before'"
            key="line-default-before"
            class="d-flex flex-column"
            style="gap: 10px;"
          >
            <div class="d-flex flex-column" style="gap: 8px;">
              <div class="black--text font-weight-medium">訊息內容</div>
              <v-textarea
                v-model="editableMessage.content.text"
                :rules="rules.defaultText"
                outlined
                background-color="white"
                hide-details="auto"
              />
            </div>
            <div class="d-flex flex-column" style="gap: 8px;">
              <div class="black--text font-weight-medium">現成卡片示意圖（點擊圖片顯示完整卡片內容）</div>
              <v-sheet
                max-width="150"
                max-height="150"
                class="overflow-hidden d-flex align-center justify-center"
                rounded
                role="button"
                style="cursor: pointer;"
                @click="openImageDialog(assetURL('/asset/crm/BeforeTreatmentCareDefaultCard.png'))"
              >
                <v-img
                  :src="assetURL('/asset/crm/BeforeTreatmentCareDefaultCard.png')"
                  max-width="150"
                  max-height="150"
                  contain
                />
              </v-sheet>
            </div>
          </div>

          <div v-if="type === 'post-op'" class="d-flex flex-column" style="gap: 10px;">
            <!-- Post-op specific fields -->
            <div class="d-flex flex-column" style="gap: 8px;">
              <div class="black--text font-weight-medium">發送頻率</div>
              <div class="d-flex align-center flex-wrap" style="gap: 8px;">
                <v-select
                  v-model="editableMessage.content.offset_direction"
                  :items="offsetDirectionOptions"
                  outlined
                  dense
                  background-color="white"
                  hide-details="auto"
                  style="max-width: 110px; min-width: 80px;"
                />
                <div class="d-flex align-center">
                  <v-text-field
                    v-model="editableMessage.content.start_offset_days"
                    :rules="rules.frequency"
                    type="number"
                    outlined
                    min="0"
                    dense
                    background-color="white"
                    class="mr-2"
                    hide-details="auto"
                    style="max-width: 100px; min-width: 60px;"
                  />
                  <span class="mr-2">天，</span>
                  <v-select
                    v-model="selectedHour"
                    :items="hours"
                    :rules="rules.hour"
                    label="時"
                    outlined
                    dense
                    background-color="white"
                    class="mr-2"
                    hide-details="auto"
                    style="max-width: 100px; min-width: 60px;"
                  />
                  <span class="mr-2">:</span>
                  <v-select
                    v-model="selectedMinute"
                    :items="minutes"
                    :rules="rules.minute"
                    label="分"
                    outlined
                    dense
                    background-color="white"
                    hide-details="auto"
                    style="max-width: 100px; min-width: 60px;"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </v-card-text>
    </v-card>

    <v-dialog
      v-if="showImageDialog"
      v-model="showImageDialog"
      max-width="500"
      @input="onImageDialogInput"
    >
      <v-card>
        <div class="text-right px-2 pt-2">
          <v-btn icon color="grey-5" @click="closeImageDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
        <v-sheet class="pa-4">
          <v-img :key="dialogImageUrl" :src="dialogImageUrl" contain/>
        </v-sheet>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import ImageUploadTool from "@/components/utils/ImageUploadTool.vue";
import { assetURL } from "@/utils/filters";

export default {
  name: 'MessageContentCard',
  components: {ImageUploadTool},
  props: {
    message: {
      type: Object,
      required: true,
    },
    type: {
      type: String,
      required: true,
    },
    imgLimit: {
      type: Number,
      default: 1
    },
    showDelete: {
      type: Boolean,
      default: false
    },
    rules: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      showImageDialog: false,
      dialogImageUrl: '',
      hours: Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0')),
      minutes: Array.from({ length: 12 }, (_, i) => (i * 5).toString().padStart(2, '0')),
      defaultTextByType: {
        line_default: '看完診後，您是否有感到不適的地方，或是否需要反應您的狀況給醫師或診所。',
        line_default_before: '看診時間將近，您是否有任何想先了解的問題呢？請點擊按鈕回覆～',
      },
      allowedDirectionsByType: {
        line_default: ['AFTER'],
        line_default_before: ['BEFORE'],
      },
    }
  },
  computed: {
    editableMessage: {
      get() {
        return this.message;
      },
      set(value) {
        this.$emit('update:message', value);
      },
    },
    messageStyleOptions() {
      const postOpOptions = [
        { text: '術後關懷預設訊息', value: 'line_default' },
        { text: '術前關懷預設訊息', value: 'line_default_before' },
        { text: '純訊息文字+圖', value: 'line_text_image' },
        { text: '客製化 JSON', value: 'line_flex' },
      ];
      const preOpOptions = [
        { text: '純訊息文字 + 圖片（無按鈕）', value: 'line_text_image' },
        { text: '客製化 JSON 格式', value: 'line_flex' },
      ];

      return this.type === 'post-op' ? postOpOptions : preOpOptions;
    },
    offsetDirectionOptions() {
      const allOptions = [
        { text: '看診後', value: 'AFTER' },
        { text: '看診前', value: 'BEFORE' },
      ];
      const allowed = this.allowedDirectionsByType[this.editableMessage.type];
      if (!allowed) return allOptions;
      return allOptions.filter(opt => allowed.includes(opt.value));
    },
    selectedHour: {
      get() {
        if (!this.editableMessage.content.send_at_time) return null;
        return this.editableMessage.content.send_at_time.split(':')[0];
      },
      set(newHour) {
        const minute = this.selectedMinute || '00';
        this.$set(this.editableMessage.content, 'send_at_time', `${newHour}:${minute}`);
      }
    },
    selectedMinute: {
      get() {
        if (!this.editableMessage.content.send_at_time) return null;
        return this.editableMessage.content.send_at_time.split(':')[1];
      },
      set(newMinute) {
        const hour = this.selectedHour || '09';
        this.$set(this.editableMessage.content, 'send_at_time', `${hour}:${newMinute}`);
      }
    }
  },
  watch: {
    'editableMessage.type'(newType, oldType) {
      this.closeImageDialog()

      const newDefault = this.defaultTextByType[newType]
      if (newDefault) {
        const oldDefault = this.defaultTextByType[oldType]
        const currentText = this.editableMessage.content?.text
        if (!currentText?.trim() || currentText === oldDefault) {
          this.$set(this.editableMessage.content, 'text', newDefault)
        }
      }

      const allowed = this.allowedDirectionsByType[newType]
      if (allowed && !allowed.includes(this.editableMessage.content?.offset_direction)) {
        this.$set(this.editableMessage.content, 'offset_direction', allowed[0])
      }
    }
  },
  created() {
    if (!this.editableMessage.content) {
      this.$set(this.editableMessage, 'content', {});
    }
    if (this.type === 'post-op') {
      if (this.editableMessage.content.send_at_time === undefined) {
        this.$set(this.editableMessage.content, 'send_at_time', null);
      }
      // 初始化 offset_direction，預設為 AFTER（看診後）
      if (this.editableMessage.content.offset_direction === undefined) {
        this.$set(this.editableMessage.content, 'offset_direction', 'AFTER');
      }
      // 初始化 title
      if (this.editableMessage.content.title === undefined) {
        this.$set(this.editableMessage.content, 'title', '');
      }
    }
    // Ensure images array exists for the upload tool
    if (!this.editableMessage.content.images) {
      this.$set(this.editableMessage.content, 'images', []);
    }
    // 新建訊息時填入對應 type 的預設文字
    const defaultText = this.defaultTextByType[this.editableMessage.type]
    if (defaultText && !this.editableMessage.content?.text?.trim()) {
      this.$set(this.editableMessage.content, 'text', defaultText)
    }
  },
  methods: {
    assetURL,
    openImageDialog(imageUrl) {
      if (!imageUrl) return;
      this.dialogImageUrl = imageUrl;
      this.showImageDialog = true;
    },
    closeImageDialog() {
      this.showImageDialog = false;
      this.dialogImageUrl = '';
    },
    onImageDialogInput(value) {
      if (!value) {
        this.closeImageDialog();
      }
    },
    onImageAdded(newImages) {
      this.editableMessage.content.images.push(...newImages);
      this.$emit('content-changed');
    },
    onImageRemoved(imageIndex) {
      this.editableMessage.content.images.splice(imageIndex, 1);
      this.$emit('content-changed');
    },
    getImgSrc(value) {
      if (typeof value === 'string') {
        return value;
      } else {
        return URL.createObjectURL(value);
      }
    },
  }
};
</script>
